import { useState, useCallback, useEffect, useMemo } from 'react';
import { useGridApiRef } from '@material-ui/data-grid';
import { isRowMissing } from '../utils/rowHelpers';

// Offset to account for sticky header when scrolling to rows
// Sticky header top position (100px) + header height (~52px) + padding (10px)
const STICKY_HEADER_OFFSET = 100 + 52 + 10; // 162px

export const useRowNavigation = (
  filteredRows,
  pageSize,
  showOnlyChanged,
  gridContainerRef
) => {
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSizeState, setPageSizeState] = useState(pageSize);
  const apiRef = useGridApiRef();
  const missingRows = useMemo(() => {
    return (filteredRows || []).filter((row) => isRowMissing(row));
  }, [filteredRows]);

  // Reset selected row when filters change
  useEffect(() => {
    setSelectedRowId(null);
    setPage(0);
  }, [showOnlyChanged]);

  // Reset selected row when filtered rows change significantly
  // This ensures navigation stays in sync with what's actually displayed
  useEffect(() => {
    // Only reset if we have a selected row and it's not in the current filtered rows
    if (selectedRowId && filteredRows.length > 0) {
      const isSelectedRowInFiltered = filteredRows.some((row) => row.id === selectedRowId);
      if (!isSelectedRowInFiltered) {
        setSelectedRowId(null);
        setPage(0);
      }
    }
  }, [filteredRows, selectedRowId]);

  // Handle page size change
  useEffect(() => {
    setPageSizeState(pageSize);
  }, [pageSize]);

  // Helper function to scroll window to make row visible
  const scrollWindowToRow = useCallback(
    (rowId, delay = 150) => {
      // Wait a bit for DataGrid to render the row
      setTimeout(() => {
        // Use a safer approach that doesn't rely on CSS selector syntax
        // This avoids issues with special characters (Hebrew, quotes, etc.) in rowId
        // Find all rows with data-id attribute and filter by exact match
        const allRows = document.querySelectorAll('[role="row"][data-id]');
        const rowElement = Array.from(allRows).find(
          (row) => row.getAttribute('data-id') === String(rowId)
        );

        if (rowElement) {
          // Get the element's position relative to the viewport
          const rect = rowElement.getBoundingClientRect();
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft =
            window.pageXOffset || document.documentElement.scrollLeft;

          // Calculate the position we want to scroll to
          // Use constant offset to account for sticky header
          const targetY = rect.top + scrollTop - STICKY_HEADER_OFFSET;

          // Smooth scroll to the row
          window.scrollTo({
            top: Math.max(0, targetY), // Ensure we don't scroll to negative position
            left: scrollLeft,
            behavior: 'smooth'
          });
        } else if (gridContainerRef?.current) {
          // Fallback: scroll to the grid container if row not found
          const containerRect =
            gridContainerRef.current.getBoundingClientRect();
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          
          // Use constant offset to account for sticky header
          const targetY = containerRect.top + scrollTop - STICKY_HEADER_OFFSET;
          window.scrollTo({
            top: Math.max(0, targetY),
            behavior: 'smooth'
          });
        }
      }, delay);
    },
    [gridContainerRef]
  );

  // Handle row click to track selected row
  const handleRowClick = useCallback((params) => {
    setSelectedRowId(params.id);
  }, []);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSizeState(newPageSize);
    setPage(0); // Reset to first page when page size changes
  }, []);

  // Navigate to next/previous missing row
  const navigateToMissingRow = useCallback(
    (direction) => {
      if (filteredRows.length === 0) return;

      let currentIndex = -1;
      if (selectedRowId) {
        currentIndex = filteredRows.findIndex(
          (row) => row.id === selectedRowId
        );
      }

      const targetRowIndex = findIndex(
        filteredRows,
        (row) => isRowMissing(row),
        {
          direction: direction === 1 ? 'forward' : 'backward',
          start: currentIndex
        }
      );

      const targetRow = filteredRows[targetRowIndex];

      if (targetRowIndex === -1) return;

      const targetPage = Math.floor(targetRowIndex / pageSizeState);
      const rowIndexInPage = targetRowIndex % pageSizeState;

      // Update selected row first
      setSelectedRowId(targetRow.id);

      // Navigate to the correct page if needed
      if (targetPage !== page) {
        setPage(targetPage);
        // Wait for page to update, then scroll to the row within the page
        setTimeout(() => {
          if (apiRef?.current?.scrollToIndexes) {
            apiRef.current.scrollToIndexes({ rowIndex: rowIndexInPage });
            // Also scroll window to make row visible (longer delay for page change)
            scrollWindowToRow(targetRow.id, 100);
          } else {
            // Fallback: just scroll window if apiRef not ready
            scrollWindowToRow(targetRow.id, 100);
          }
        }, 250);
      } else {
        // Same page, just scroll to the row within the page
        setTimeout(() => {
          if (apiRef?.current?.scrollToIndexes) {
            apiRef.current.scrollToIndexes({ rowIndex: rowIndexInPage });
            // Also scroll window to make row visible
            scrollWindowToRow(targetRow.id);
          } else {
            // Fallback: just scroll window if apiRef not ready
            scrollWindowToRow(targetRow.id);
          }
        }, 100);
      }
    },
    [
      missingRows,
      selectedRowId,
      filteredRows,
      pageSizeState,
      page,
      apiRef,
      scrollWindowToRow
    ]
  );

  // Navigate to next missing row
  const handleNextMissing = useCallback(() => {
    navigateToMissingRow(1);
  }, [navigateToMissingRow]);

  // Navigate to previous missing row
  const handlePreviousMissing = useCallback(() => {
    navigateToMissingRow(-1);
  }, [navigateToMissingRow]);

  // Check if navigation buttons should be disabled
  const canNavigateNext =
    missingRows.length > 0 &&
    (!selectedRowId ||
      missingRows.findIndex((row) => row.id === selectedRowId) <
        missingRows.length - 1);

  const canNavigatePrevious =
    missingRows.length > 0 &&
    (!selectedRowId ||
      missingRows.findIndex((row) => row.id === selectedRowId) > 0);

  return {
    selectedRowId,
    page,
    pageSize: pageSizeState,
    apiRef,
    handleRowClick,
    handlePageChange,
    handlePageSizeChange,
    handleNextMissing,
    handlePreviousMissing,
    canNavigateNext,
    canNavigatePrevious
  };
};

/**
 * @param {Array} arr
 * @param {(value:any, index:number, array:Array) => boolean} predicate
 * @param {{
 *   start?: number,
 *   direction?: 'forward' | 'backward'
 * }} options
 * @returns {number}
 */
function findIndex(arr, predicate, options = {}) {
  const {
    start = options.direction === 'backward' ? arr.length - 1 : 0,
    direction = 'forward'
  } = options;

  if (direction === 'backward') {
    for (let i = start - 1; i >= 0; i--) {
      if (predicate(arr[i], i, arr)) return i;
    }
  } else {
    for (let j = start + 1; j < arr.length; j++) {
      console.debug('j', j);
      if (predicate(arr[j], j, arr)) return j;
    }
  }

  return -1;
}
